import { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Deck } from '@deck.gl/core';
import { TripsLayer } from '@deck.gl/geo-layers';
import { PathLayer, ScatterplotLayer } from '@deck.gl/layers';

export interface HeatmapPoint { lat: number; lon: number; value: number; }
export interface TripCoord { lon: number; lat: number; t: number; rsrp?: number | null; }

export interface SessionMapProps {
  /** Ordered coords for the path (already cleaned). */
  pathCoords: [number, number][];
  /** Aggregate heatmap points (rsrp-normalized). */
  heatmapPoints?: HeatmapPoint[];
  /** Time-stamped coords for animated replay. */
  tripCoords?: TripCoord[];
  /** 0..1 fraction of the trip to play to (controlled by parent slider). */
  cursorFraction?: number;
  /** Auto-play (1× = real time, 60× etc — actually used by parent to advance cursorFraction). */
}

const STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    'carto-dark': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
        'https://d.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
      ],
      tileSize: 256,
      attribution: '© OSM © CARTO',
    },
  },
  layers: [{ id: 'carto-dark', type: 'raster', source: 'carto-dark' }],
};

// RSRP → RGB. Industry buckets used in TEMS / Nemo trails.
function rsrpColor(rsrp: number | null | undefined): [number, number, number, number] {
  if (rsrp == null) return [148, 163, 184, 220];      // grey — unknown
  if (rsrp >= -80)  return [34, 197, 94, 230];        // green — excellent
  if (rsrp >= -90)  return [132, 204, 22, 230];       // lime — good
  if (rsrp >= -100) return [250, 204, 21, 230];       // yellow — fair
  if (rsrp >= -110) return [249, 115, 22, 230];       // orange — poor
  return [239, 68, 68, 230];                          // red — very poor
}

export default function SessionMap({ pathCoords, heatmapPoints, tripCoords, cursorFraction }: SessionMapProps) {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const deckContainer = useRef<HTMLCanvasElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const deckRef = useRef<Deck | null>(null);
  const [ready, setReady] = useState(false);

  // ── Init MapLibre + deck.gl side-by-side, sync viewports ────────────────
  useEffect(() => {
    if (!mapContainer.current || !deckContainer.current) return;

    const initialView = pickInitialView(pathCoords, tripCoords);

    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: STYLE,
      center: [initialView.longitude, initialView.latitude],
      zoom: initialView.zoom,
      interactive: true,
    });
    mapRef.current = map;
    setTimeout(() => map.resize(), 100);
    setTimeout(() => map.resize(), 500);

    const deck = new Deck({
      canvas: deckContainer.current,
      width: '100%',
      height: '100%',
      initialViewState: initialView,
      controller: false, // map drives the camera
      layers: [],
    });
    deckRef.current = deck;

    const syncDeck = () => {
      const c = map.getCenter();
      deck.setProps({
        viewState: {
          longitude: c.lng,
          latitude: c.lat,
          zoom: map.getZoom(),
          pitch: map.getPitch(),
          bearing: map.getBearing(),
        },
      });
    };
    map.on('move', syncDeck);
    map.on('load', () => {
      syncDeck();
      // fit to data bbox once
      const bounds = computeBounds(pathCoords);
      if (bounds) map.fitBounds(bounds, { padding: 60, maxZoom: 16, duration: 600 });
      setReady(true);
    });

    return () => {
      map.remove(); mapRef.current = null;
      deck.finalize(); deckRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Rebuild deck layers on data change ──────────────────────────────────
  useEffect(() => {
    if (!ready || !deckRef.current) return;

    const layers: any[] = [];

    // RSRP-colored path: one short segment per consecutive trip pair, colored by avg RSRP.
    if (tripCoords && tripCoords.length >= 2) {
      const segments: Array<{ path: [number, number][]; rsrp: number | null }> = [];
      for (let i = 0; i < tripCoords.length - 1; i++) {
        const a = tripCoords[i], b = tripCoords[i + 1];
        const rsrpA = a.rsrp; const rsrpB = b.rsrp;
        const avg = rsrpA != null && rsrpB != null ? (rsrpA + rsrpB) / 2
          : (rsrpA ?? rsrpB ?? null);
        segments.push({ path: [[a.lon, a.lat], [b.lon, b.lat]], rsrp: avg });
      }
      layers.push(
        new PathLayer<{ path: [number, number][]; rsrp: number | null }>({
          id: 'path-rsrp',
          data: segments,
          getPath: (d) => d.path,
          getColor: (d) => rsrpColor(d.rsrp),
          widthMinPixels: 4,
          widthMaxPixels: 8,
          jointRounded: true,
          capRounded: true,
        }),
      );
    } else if (pathCoords.length >= 2) {
      // fallback when no rsrp info: plain blue path
      layers.push(
        new PathLayer<{ path: [number, number][] }>({
          id: 'path',
          data: [{ path: pathCoords }],
          getPath: (d) => d.path,
          getColor: [96, 165, 250, 220],
          widthMinPixels: 3,
          widthMaxPixels: 6,
          jointRounded: true,
          capRounded: true,
        }),
      );
    }

    if (tripCoords && tripCoords.length >= 2 && cursorFraction != null) {
      const t0 = tripCoords[0].t;
      const tN = tripCoords[tripCoords.length - 1].t;
      const currentTime = t0 + cursorFraction * (tN - t0);
      layers.push(
        new TripsLayer<{ path: [number, number][]; timestamps: number[] }>({
          id: 'trip',
          data: [{
            path: tripCoords.map((p) => [p.lon, p.lat]),
            timestamps: tripCoords.map((p) => p.t),
          }],
          getPath: (d) => d.path,
          getTimestamps: (d) => d.timestamps,
          getColor: [34, 197, 94, 255],
          widthMinPixels: 5,
          currentTime,
          trailLength: (tN - t0) * 0.15,
          fadeTrail: true,
          jointRounded: true,
          capRounded: true,
        }),
      );

      // current position marker
      const idx = nearestIdx(tripCoords.map((p) => p.t), currentTime);
      const here = tripCoords[idx];
      if (here) {
        layers.push(
          new ScatterplotLayer<TripCoord>({
            id: 'cursor',
            data: [here],
            getPosition: (d) => [d.lon, d.lat],
            getFillColor: [34, 197, 94, 255],
            getLineColor: [255, 255, 255, 255],
            getRadius: 8,
            radiusMinPixels: 8,
            radiusMaxPixels: 12,
            lineWidthMinPixels: 2,
            stroked: true,
          }),
        );
      }
    }

    deckRef.current.setProps({ layers });
  }, [ready, pathCoords, heatmapPoints, tripCoords, cursorFraction]);

  return (
    <>
      <div ref={mapContainer} style={{ position: 'absolute', inset: 0 }} />
      <canvas
        ref={deckContainer}
        style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
      />
    </>
  );
}

function nearestIdx(arr: number[], t: number): number {
  let lo = 0, hi = arr.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < t) lo = mid + 1; else hi = mid;
  }
  return lo;
}

function pickInitialView(
  pathCoords: [number, number][],
  trip?: TripCoord[],
): { longitude: number; latitude: number; zoom: number; pitch: number; bearing: number } {
  if (pathCoords.length > 0) {
    const [lon, lat] = pathCoords[0];
    return { longitude: lon, latitude: lat, zoom: 12, pitch: 0, bearing: 0 };
  }
  if (trip && trip.length > 0) {
    return { longitude: trip[0].lon, latitude: trip[0].lat, zoom: 12, pitch: 0, bearing: 0 };
  }
  return { longitude: 2.35, latitude: 46.8, zoom: 5, pitch: 0, bearing: 0 };
}

function computeBounds(coords: [number, number][]): [[number, number], [number, number]] | null {
  if (coords.length < 2) return null;
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lat < minLat) minLat = lat;
    if (lon > maxLon) maxLon = lon;
    if (lat > maxLat) maxLat = lat;
  }
  return [[minLon, minLat], [maxLon, maxLat]];
}
